import { IconComponentProps } from '@nexelem/icons';
import {
  ChangeEvent,
  ComponentProps,
  FC,
  FocusEventHandler,
  ReactNode,
  RefCallback,
  RefObject,
  useEffect,
  useId,
  useMemo,
} from 'react';
import { Input } from './Input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation, faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { useIMask } from 'react-imask';
import clsx from 'clsx';
import { VirtualKeyboardPopper, VirtualKeyboardPopperEnable } from '../VirtualKeyboard';
import { Mask, MaskType } from './masks';
import { FactoryOpts } from 'imask/esm/index';
import { mergeRefs, normalizeTime } from '@nexelem/utils';
import { DateRange } from '../Calendar';

type InputValue = string | number | Date | DateRange;

export type TextInputSize = 'small' | 'default' | 'jumbo' | 'extraJumbo';

export type TextFieldProps<T extends InputValue> = Omit<
  ComponentProps<'input'>,
  'size' | 'onChange' | 'value' | 'defaultValue' | 'type' | 'ref'
> & {
  value?: T;
  className?: string;
  size?: TextInputSize;
  /** @deprecated use type instead */
  maskOptions?: FactoryOpts;
  label?: string;
  inputClassName?: string;
  /** Show error border, without errorMessage, not required when errorMessage is provided */
  showErrorBorder?: boolean;
  errorMessage?: string;
  helperText?: string;
  enableClearButton?: boolean;
  /** Show required asterisk */
  required?: boolean;
  StartIcon?: FC<IconComponentProps>;
  EndIcon?: ReactNode;
  /** Enable multiline, use textarea instead of input */
  multiline?: boolean;
  onChange?: (value: T) => void;
  /** Only for multiline inputs */
  rows?: number;
  /** Enable virtual keyboard */
  enableVirtualKeyboard?: VirtualKeyboardPopperEnable;
  type?: MaskType;
  disableCharCounter?: boolean;
  ref?: RefObject<HTMLInputElement | null> | RefCallback<HTMLInputElement | null>;
};

/**
 * TextField extended Input component, with support for error message, helper text, mask and clear button.
 *  */
export const TextField = <T extends InputValue>({
  StartIcon,
  showErrorBorder,
  errorMessage,
  required,
  className,
  onChange,
  maskOptions,
  type = 'text',
  label,
  value: inputValue,
  helperText,
  enableClearButton,
  readOnly,
  maxLength,
  disableCharCounter = false,
  onFocus,
  onBlur,
  enableVirtualKeyboard,
  id,
  ref: inputRef,
  ...props
}: TextFieldProps<T>) => {
  const labelId = useId();

  const { ref, maskRef, value, unmaskedValue, clear } = useMask({
    maskOptions,
    type,
    onChange: onChange as (value: InputValue) => void,
    value: inputValue as InputValue,
  });

  const isExtraJumbo = props.size === 'extraJumbo';

  /**
   * IMask doesn't support number type, so we need to use text type instead.
   * eg. 1,500,000 is presented as string, not number
   * */
  const inputType = type !== 'password' ? 'text' : type;

  const handleVirtualKeyboardChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (maskRef.current) {
      maskRef.current.unmaskedValue = e.target.value;
    }
  };

  const onBlurNoramlizeTime: FocusEventHandler<HTMLInputElement> = (e) => {
    if (type === 'time' && e.target.value) {
      const normalizedTime = normalizeTime(e.target.value);
      onChange?.(normalizedTime as T);
    }

    onBlur?.(e);
  };

  const mergedRef = mergeRefs([ref, inputRef]);

  return (
    <VirtualKeyboardPopper
      enable={enableVirtualKeyboard}
      maxLength={maxLength}
      value={unmaskedValue}
      onChange={handleVirtualKeyboardChange}
      onClose={onBlurNoramlizeTime}
      onOpen={onFocus}
    >
      {({ onOpen, onClose }) => (
        <div className={className}>
          {label && (
            <label
              className={clsx('flex gap-1 pb-2 pl-2 text-nowrap', isExtraJumbo ? 'text-t20-sb' : 'text-t14-sb')}
              htmlFor={id || labelId}
            >
              {required && !readOnly && <span className="text-icon-error">*</span>}
              <span className="text-text-secondary">{label}</span>
            </label>
          )}
          <Input
            {...props}
            error={!!errorMessage || showErrorBorder}
            hasClearButton={!props.disabled && enableClearButton && unmaskedValue.length > 0}
            id={id || labelId}
            maxLength={maxLength}
            readOnly={readOnly}
            ref={mergedRef}
            StartIcon={StartIcon && <StartIcon />}
            type={inputType}
            onBlur={onClose}
            onClear={clear}
            onFocus={onOpen}
          />
          {(errorMessage || helperText || maxLength) && (
            <div className={clsx('mt-2 ml-2 flex items-center gap-2', isExtraJumbo ? 'text-t20-sb' : 'text-t14-sb')}>
              {(errorMessage || helperText) && (
                <>
                  <span className="flex h-5 w-5 items-center justify-center">
                    {errorMessage ? (
                      <FontAwesomeIcon className="text-icon-error" icon={faCircleExclamation} />
                    ) : (
                      <FontAwesomeIcon className="text-icon-primary" icon={faCircleInfo} />
                    )}
                  </span>
                  <span className={clsx(errorMessage ? 'text-text-state-error-light' : 'text-text-secondary')}>
                    {errorMessage || helperText}
                  </span>
                </>
              )}
              {maxLength && !disableCharCounter && !readOnly && (
                <span className="mr-1 ml-auto text-text-secondary">
                  {value.length}/{maxLength}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </VirtualKeyboardPopper>
  );
};

type UseMaskProps = {
  type: MaskType;
  onChange?: (value: InputValue) => void;
  value?: InputValue;
  maskOptions?: FactoryOpts;
};

const useMask = ({ type, onChange, value: inputValue, maskOptions }: UseMaskProps) => {
  const mask = useMemo(() => maskOptions ?? Mask[type](), [type, maskOptions]);
  const { ref, maskRef, value, setValue, setTypedValue, setUnmaskedValue, unmaskedValue, typedValue } =
    useIMask<HTMLInputElement>(mask, {
      onAccept: (value, maskRef) => {
        if (typeof maskRef.typedValue === 'string') {
          onChange?.(value);
        } else {
          onChange?.(maskRef.typedValue);
        }
      },
      defaultTypedValue: inputValue,
    });

  const clear = () => {
    if (maskRef.current) {
      maskRef.current.value = '';
    }
  };

  useEffect(() => {
    if (typeof inputValue !== 'undefined' && typedValue !== inputValue && maskRef.current) {
      maskRef.current.typedValue = inputValue;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, maskRef]);

  return {
    ref,
    maskRef,
    value,
    unmaskedValue,
    clear,
    setTypedValue,
    setValue,
    setUnmaskedValue,
  };
};

TextField.displayName = 'TextField';
